import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const proximityModule: ChecklistModuleDefinition = {
  code: 'proximity',
  name: 'Proximity & Location',
  description: 'Distance to services, towns, and amenities',
  categories: [
    {
      code: 'basic_services',
      name: 'Basic Services',
      items: [
        {
          code: 'nearest_town',
          title: 'Nearest town/city',
          description: 'Distance to nearest town',
          isGroupTask: false,
          dataFields: [
            { key: 'town_name', label: 'Town Name', type: 'string' },
            { key: 'population', label: 'Population', type: 'number' },
            { key: 'distance_miles', label: 'Distance', type: 'number', unit: 'miles' },
            { key: 'drive_time', label: 'Drive Time', type: 'number', unit: 'minutes' },
          ],
        },
        {
          code: 'grocery_shopping',
          title: 'Grocery stores',
          description: 'Distance to grocery shopping',
          isGroupTask: false,
          dataFields: [
            { key: 'nearest_grocery', label: 'Nearest Grocery', type: 'string' },
            { key: 'distance_miles', label: 'Distance', type: 'number', unit: 'miles' },
            { key: 'drive_time', label: 'Drive Time', type: 'number', unit: 'minutes' },
            { key: 'full_service_distance', label: 'Full Service Store', type: 'number', unit: 'miles' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Grocery >30 minutes away' },
            { flag: 'red', condition: 'Grocery >45 minutes away' },
          ],
        },
        {
          code: 'hardware_farm_supply',
          title: 'Hardware/farm supply',
          description: 'Distance to hardware and farm stores',
          isGroupTask: false,
          dataFields: [
            { key: 'hardware_store', label: 'Nearest Hardware', type: 'string' },
            { key: 'hardware_distance', label: 'Distance', type: 'number', unit: 'miles' },
            { key: 'farm_supply', label: 'Nearest Farm Supply', type: 'string' },
            { key: 'farm_supply_distance', label: 'Farm Supply Distance', type: 'number', unit: 'miles' },
          ],
        },
        {
          code: 'fuel_station',
          title: 'Fuel stations',
          description: 'Nearest gas station',
          isGroupTask: false,
          dataFields: [
            { key: 'nearest_gas', label: 'Nearest Gas Station', type: 'string' },
            { key: 'distance_miles', label: 'Distance', type: 'number', unit: 'miles' },
            { key: 'diesel_available', label: 'Diesel Available', type: 'boolean' },
          ],
        },
      ],
    },
    {
      code: 'healthcare',
      name: 'Healthcare',
      items: [
        {
          code: 'hospital',
          title: 'Hospital/emergency room',
          description: 'Distance to hospital',
          isGroupTask: false,
          dataFields: [
            { key: 'hospital_name', label: 'Nearest Hospital', type: 'string' },
            { key: 'distance_miles', label: 'Distance', type: 'number', unit: 'miles' },
            { key: 'drive_time', label: 'Drive Time', type: 'number', unit: 'minutes' },
            { key: 'trauma_level', label: 'Trauma Level', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Hospital >45 minutes away' },
            { flag: 'yellow', condition: 'Hospital >30 minutes away' },
          ],
        },
        {
          code: 'medical_services',
          title: 'Medical services',
          description: 'Doctors, dentists, pharmacies',
          isGroupTask: false,
          dataFields: [
            { key: 'doctor_distance', label: 'Doctor Distance', type: 'number', unit: 'miles' },
            { key: 'dentist_distance', label: 'Dentist Distance', type: 'number', unit: 'miles' },
            { key: 'pharmacy_distance', label: 'Pharmacy Distance', type: 'number', unit: 'miles' },
            { key: 'veterinarian_distance', label: 'Large Animal Vet', type: 'number', unit: 'miles' },
          ],
        },
      ],
    },
    {
      code: 'education',
      name: 'Education',
      items: [
        {
          code: 'school_district',
          title: 'School district',
          description: 'Assigned school district',
          isGroupTask: false,
          dataFields: [
            { key: 'district_name', label: 'District Name', type: 'string' },
            { key: 'district_rating', label: 'District Rating', type: 'number' },
            { key: 'elementary_school', label: 'Elementary School', type: 'string' },
            { key: 'elementary_distance', label: 'Elementary Distance', type: 'number', unit: 'miles' },
            { key: 'high_school', label: 'High School', type: 'string' },
            { key: 'high_school_distance', label: 'High School Distance', type: 'number', unit: 'miles' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Excellent school district' },
            { flag: 'yellow', condition: 'Poor school ratings' },
          ],
        },
        {
          code: 'bus_service',
          title: 'School bus service',
          description: 'Bus pickup availability',
          isGroupTask: false,
          dataFields: [
            { key: 'bus_available', label: 'Bus Service Available', type: 'boolean' },
            { key: 'pickup_location', label: 'Pickup Location', type: 'string' },
            { key: 'pickup_distance', label: 'Distance to Pickup', type: 'number', unit: 'miles' },
          ],
        },
      ],
    },
    {
      code: 'employment',
      name: 'Employment & Economy',
      items: [
        {
          code: 'employment_centers',
          title: 'Employment centers',
          description: 'Major employment areas',
          isGroupTask: false,
          dataFields: [
            { key: 'nearest_city', label: 'Nearest Major City', type: 'string' },
            { key: 'city_distance', label: 'Distance', type: 'number', unit: 'miles' },
            { key: 'commute_time', label: 'Commute Time', type: 'number', unit: 'minutes' },
            { key: 'major_employers', label: 'Major Employers', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Long commute to employment' },
          ],
        },
        {
          code: 'local_economy',
          title: 'Local economy assessment',
          description: 'Economic health of area',
          isGroupTask: false,
          dataFields: [
            { key: 'primary_industries', label: 'Primary Industries', type: 'array' },
            { key: 'economic_health', label: 'Economic Health', type: 'select', options: [
              { value: 'growing', label: 'Growing' },
              { value: 'stable', label: 'Stable' },
              { value: 'declining', label: 'Declining' },
            ]},
            { key: 'notes', label: 'Notes', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Strong growing economy' },
            { flag: 'yellow', condition: 'Declining local economy' },
          ],
        },
      ],
    },
    {
      code: 'transportation',
      name: 'Transportation',
      items: [
        {
          code: 'highway_access',
          title: 'Highway access',
          description: 'Access to major highways',
          isGroupTask: false,
          dataFields: [
            { key: 'nearest_highway', label: 'Nearest Highway', type: 'string' },
            { key: 'highway_distance', label: 'Distance', type: 'number', unit: 'miles' },
            { key: 'interstate_distance', label: 'Interstate Distance', type: 'number', unit: 'miles' },
          ],
        },
        {
          code: 'airport',
          title: 'Airport access',
          description: 'Distance to airports',
          isGroupTask: false,
          dataFields: [
            { key: 'regional_airport', label: 'Regional Airport', type: 'string' },
            { key: 'regional_distance', label: 'Regional Distance', type: 'number', unit: 'miles' },
            { key: 'major_airport', label: 'Major Airport', type: 'string' },
            { key: 'major_distance', label: 'Major Airport Distance', type: 'number', unit: 'miles' },
          ],
        },
      ],
    },
    {
      code: 'recreation_amenities',
      name: 'Recreation & Amenities',
      items: [
        {
          code: 'public_lands',
          title: 'Public lands',
          description: 'Nearby public land access',
          isGroupTask: false,
          dataFields: [
            { key: 'nearest_public_land', label: 'Nearest Public Land', type: 'string' },
            { key: 'distance', label: 'Distance', type: 'number', unit: 'miles' },
            { key: 'public_land_types', label: 'Types', type: 'array' },
            { key: 'activities', label: 'Activities', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Adjacent to public land' },
            { flag: 'green', condition: 'Public land nearby' },
          ],
        },
        {
          code: 'lakes_rivers',
          title: 'Lakes and rivers',
          description: 'Recreational water nearby',
          isGroupTask: false,
          dataFields: [
            { key: 'nearest_lake', label: 'Nearest Lake', type: 'string' },
            { key: 'lake_distance', label: 'Lake Distance', type: 'number', unit: 'miles' },
            { key: 'nearest_river', label: 'Nearest River', type: 'string' },
            { key: 'river_distance', label: 'River Distance', type: 'number', unit: 'miles' },
            { key: 'activities', label: 'Activities', type: 'array' },
          ],
        },
        {
          code: 'dining_entertainment',
          title: 'Dining and entertainment',
          description: 'Restaurants and entertainment',
          isGroupTask: false,
          dataFields: [
            { key: 'restaurant_distance', label: 'Nearest Restaurant', type: 'number', unit: 'miles' },
            { key: 'dining_options', label: 'Dining Options', type: 'string' },
            { key: 'entertainment_options', label: 'Entertainment', type: 'array' },
          ],
        },
      ],
    },
    {
      code: 'agricultural_services',
      name: 'Agricultural Services',
      items: [
        {
          code: 'ag_services',
          title: 'Agricultural services',
          description: 'Farming support services',
          isGroupTask: false,
          dataFields: [
            { key: 'feed_store_distance', label: 'Feed Store', type: 'number', unit: 'miles' },
            { key: 'large_animal_vet', label: 'Large Animal Vet', type: 'number', unit: 'miles' },
            { key: 'ag_coop_distance', label: 'Ag Co-op', type: 'number', unit: 'miles' },
            { key: 'equipment_dealer', label: 'Equipment Dealer', type: 'number', unit: 'miles' },
            { key: 'livestock_auction', label: 'Livestock Auction', type: 'number', unit: 'miles' },
          ],
        },
        {
          code: 'markets',
          title: 'Agricultural markets',
          description: 'Places to sell products',
          isGroupTask: false,
          dataFields: [
            { key: 'farmers_market', label: "Farmer's Market", type: 'number', unit: 'miles' },
            { key: 'grain_elevator', label: 'Grain Elevator', type: 'number', unit: 'miles' },
            { key: 'processing_facilities', label: 'Processing Facilities', type: 'array' },
          ],
        },
      ],
    },
  ],
};

